import { type ReactElement, useState } from 'react';

import { cx } from '@/shared/lib';
import { Icon } from '@/shared/ui/icon';

import { FieldFrame, type TextFieldProps } from './Field';
import fieldStyles from './Field.module.scss';
import styles from './PasswordField.module.scss';

function VisibilityButton({
  visible,
  disabled,
  onToggle,
}: {
  visible: boolean;
  disabled: boolean;
  onToggle: () => void;
}): ReactElement {
  const label = visible ? 'Скрыть пароль' : 'Показать пароль';
  return (
    <button
      className={styles.toggle}
      type="button"
      aria-label={label}
      aria-pressed={visible}
      title={label}
      disabled={disabled}
      onClick={onToggle}
    >
      <Icon name={visible ? 'eye-off' : 'eye'} />
    </button>
  );
}

interface PasswordControlProps {
  field: Omit<TextFieldProps, 'type' | 'label'>;
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  visible: boolean;
  onToggle: () => void;
}

function PasswordControl(props: PasswordControlProps): ReactElement {
  const { className, ...input } = props.field;
  return (
    <div className={styles.wrap}>
      <input
        id={props.id}
        type={props.visible ? 'text' : 'password'}
        className={cx(fieldStyles.control, styles.control, className)}
        aria-invalid={props.invalid || undefined}
        aria-describedby={props.describedBy}
        {...input}
      />
      <VisibilityButton
        visible={props.visible}
        disabled={input.disabled === true}
        onToggle={props.onToggle}
      />
    </div>
  );
}

/** Visibility is local to this input; toggling never submits the surrounding form. */
export function PasswordField({
  label,
  error,
  hint,
  ...input
}: Omit<TextFieldProps, 'type'>): ReactElement {
  const [visible, setVisible] = useState(false);
  return (
    <FieldFrame label={label} error={error} hint={hint}>
      {(frame) => (
        <PasswordControl
          {...frame}
          field={input}
          visible={visible}
          onToggle={() => setVisible((value) => !value)}
        />
      )}
    </FieldFrame>
  );
}
